import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Modal } from 'react-bootstrap';
import { Copy, HandThumbsUp, HandThumbsUpFill, HandThumbsDown, HandThumbsDownFill } from 'react-bootstrap-icons';
import { Link, useParams, useLocation } from 'react-router-dom';
import './Room.css';
import './ChatAccessibility.css';
import '../kbEmptyBanner.css';
import { toast } from 'react-toastify';
import { useGetChatHistoryQuery, useCreateRoomChatMutation, useUpdateChatFeedbackMutation, userApi } from '../services/user';
import { authApi, useGetUserDataQuery } from '../services/auth';
import { sendStreamMessage } from '../services/streamApi';
import copy from "copy-to-clipboard";
import { useDispatch, useSelector } from 'react-redux';
import { setNewChat } from '../services/GlobalSlice';
import { stripMarkdownForCopy, validateAnalysisFile, getAnalysisPlaceholder } from '../utils/chatUi';
import ChatHeader from './ChatHeader';
import WelcomeScreen from './WelcomeScreen';
import ChatHistoryLoader from './ChatHistoryLoader';
import PlanBanner, { ASK_BLOCKED_MESSAGE_ID } from './PlanBanner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil } from 'lucide-react';
import { markdownComponents } from '../utils/markdownComponents';
import Citations, { ReviewedAnswerBadge } from '../Citations';

import ThinkingIndicator from './ThinkingIndicator';

const applyCitationsToLastBotMessage = (msgs, { citations, hasTenantDocs, answerSource }) => {
  if (!citations?.length || !msgs?.length) return msgs;
  let lastBotIdx = -1;
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    if (msgs[i].sender === 'bot' && !msgs[i].streaming && !msgs[i].isLoading) {
      lastBotIdx = i;
      break;
    }
  }
  if (lastBotIdx === -1) {
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i].sender === 'bot') {
        lastBotIdx = i;
        break;
      }
    }
  }
  if (lastBotIdx === -1) return msgs;
  return msgs.map((m, i) =>
    i === lastBotIdx
      ? { ...m, citations, hasTenantDocs, answerSource }
      : m
  );
};

const Room = () => {
  const { roomId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { data: userData } = useGetUserDataQuery();
  const canAsk = userData?.can_ask !== false;
  const { newChat, selectedDB, selectedModule } = useSelector((state) => state.global);
  const inputPlaceholder = 'Ask anything about Oracle HCM…';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currentFeedbackId, setCurrentFeedbackId] = useState(null);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [citations, setCitations] = useState([]);
  const [hasTenantDocs, setHasTenantDocs] = useState(false);
  const [answerSource, setAnswerSource] = useState('ai_knowledge');
  const [analysisFile, setAnalysisFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [referenceDoc, setReferenceDoc] = useState(null);
  const [kbStatus, setKbStatus] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);
  const analyzeAbortRef = useRef(null);
  const isSendingRef = useRef(false);
  const currentRoomIdRef = useRef(roomId);
  const textareaRef = useRef(null);
  const chatContainerRef = useRef(null);
  const userScrolledUp = useRef(false);
  const lastScrollTop = useRef(0);
  const accumulatedText = useRef('');
  const streamingMessageIdRef = useRef(null);
  const messageRefs = useRef({}); // Object to store refs for each message
  const navCitationsRef = useRef(null);

  const {
    data: chatHistory,
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
  } = useGetChatHistoryQuery(
    { roomId },
    { skip: !roomId, refetchOnMountOrArgChange: true }
  );
  const [createRoomChat, { error }] = useCreateRoomChatMutation();
  const [isStreaming, setIsStreaming] = useState(false);
  const [updateChatFeedback] = useUpdateChatFeedbackMutation();
  const showHistoryLoader = Boolean(roomId) && (isHistoryLoading || isHistoryFetching) && messages.length === 0 && !isStreaming;

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    userScrolledUp.current = !isNearBottom;
    lastScrollTop.current = container.scrollTop;
  };

  const scrollToBottom = (force = false) => {
    const container = chatContainerRef.current;
    if (!container) return;
    if (force || !userScrolledUp.current) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0 || messages.some((m) => m.citations?.length > 0)) {
      scrollToBottom();
    }
  }, [messages, isStreaming]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error.data?.error || "An unexpected error occurred.");
    }
  }, [error]);

  useEffect(() => {
    const fetchKbStatus = async () => {
      const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('access_token');
      try {
        const res = await fetch(`${baseUrl}/tenant/knowledge-base/status/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setKbStatus(data);
        }
      } catch (_) {
        // ignore
      }
    };
    fetchKbStatus();
  }, []);

  const handleSendMessage = async (forcedText = null) => {
    const messageText = (forcedText ?? input).trim();
    if (!messageText && !analysisFile) return;
    if (!canAsk) return;
    if (isSendingRef.current || isStreaming || isAnalyzing) return;

    if (analysisFile && !messageText) {
      toast.info('Type a question about your document');
      return;
    }

    if (analysisFile && messageText) {
      await handleAnalyzeDocument(messageText);
      return;
    }

    isSendingRef.current = true;
    try {
    setCitations([]);
    setHasTenantDocs(false);
    setAnswerSource('ai_knowledge');
    userScrolledUp.current = false;
    const question = messageText;
    const userMessage = { sender: 'user', text: messageText, id: `user-${Date.now()}` };
    setMessages(prev => [...prev, userMessage]);
    if (forcedText == null) setInput('');

    const placeholderId = `stream-${Date.now()}`;
    accumulatedText.current = '';
    streamingMessageIdRef.current = placeholderId;
    const botPlaceholder = {
      sender: 'bot',
      text: '',
      id: placeholderId,
      feedback: null,
      streaming: true,
      citations: [],
      hasTenantDocs: false,
      answerSource: 'ai_knowledge',
    };
    setMessages(prev => [...prev, botPlaceholder]);
    setIsStreaming(true);
    scrollToBottom(true);

    let streamedOk = await sendStreamMessage(
      { user_question: question, roomId, database_id: selectedDB, module_hint: selectedModule },
      {
        onToken: (token) => {
          accumulatedText.current += token;
          setMessages(prev =>
            prev.map(m => (m.id === placeholderId ? { ...m, text: (m.text || '') + token } : m))
          );
          scrollToBottom();
        },
        onDone: (data) => {
          const streamId = streamingMessageIdRef.current;
          const finalText = accumulatedText.current || 'No response received.';
          setMessages(prev => {
            const exists = prev.find(m => m.id === streamId);
            if (!exists) {
              return [...prev, {
                sender: 'bot',
                text: finalText,
                id: data.chat_id,
                feedback: null,
                streaming: false,
                download_url: data.download_url,
                citations: [],
                hasTenantDocs: false,
                answerSource: 'ai_knowledge',
                suggestions: [],
              }];
            }
            return prev.map(m =>
              m.id === streamId
                ? {
                    ...m,
                    id: data.chat_id,
                    streaming: false,
                    text: finalText,
                    download_url: data.download_url,
                    // Preserve citations that arrived via onCitations before onDone
                    citations: m.citations?.length > 0 ? m.citations : [],
                    hasTenantDocs: m.hasTenantDocs ?? false,
                    answerSource: m.answerSource ?? 'ai_knowledge',
                  }
                : m
            );
          });
          setIsStreaming(false);
          streamingMessageIdRef.current = null;
          accumulatedText.current = '';
          scrollToBottom(true);
          dispatch(userApi.util.invalidateTags(['NEW_CHAT', { type: 'CHAT_HISTORY', id: roomId }]));
        },
        onCitations: (parsed) => {
          const citationData = {
            citations: parsed.citations || [],
            hasTenantDocs: parsed.has_tenant_docs || false,
            answerSource: parsed.answer_source || 'ai_knowledge',
          };
          setCitations(citationData.citations);
          setHasTenantDocs(citationData.hasTenantDocs);
          setAnswerSource(citationData.answerSource);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === placeholderId ? { ...m, ...citationData } : m
            )
          );
        },
        onSuggestions: (questions) => {
          setMessages(prev =>
            prev.map(m =>
              m.id === placeholderId ? { ...m, suggestions: questions } : m
            )
          );
        },
        onError: (errMsg, code) => {
          if (code) dispatch(authApi.util.invalidateTags(['USER']));
          if (errMsg?.includes?.("429")) {
            toast.error("You've sent a lot of messages! Please wait a moment before continuing.");
          } else {
            toast.error(errMsg || 'Stream failed. Trying regular send...');
          }
          setIsStreaming(false);
        },
      }
    );

    if (!streamedOk) {
      setMessages(prev => prev.filter(m => m.id !== placeholderId));
      try {
        const payload = { user_question: question, roomId, database_id: selectedDB };
        const response = await createRoomChat(payload).unwrap();
        if (Array.isArray(response.citations)) setCitations(response.citations);
        if (response.has_tenant_docs !== undefined) setHasTenantDocs(response.has_tenant_docs);
        if (response.answer_source) setAnswerSource(response.answer_source);
        setMessages(prev => [
          ...prev,
          {
            sender: 'bot',
            text: response.bot_answer || 'No response received.',
            id: response.chat_id,
            feedback: null,
            download_url: response.download_url,
            citations: response.citations || [],
            hasTenantDocs: response.has_tenant_docs ?? false,
            answerSource: response.answer_source || 'ai_knowledge',
          },
        ]);
      } catch (err) {
        if (err?.data?.code) dispatch(authApi.util.invalidateTags(['USER']));
        const status = err?.status;
        if (status === 429) {
          toast.error("You've sent a lot of messages! Please wait a moment before continuing.");
        } else {
          toast.error('Failed to send message.');
        }
      }
    }
    } finally {
      isSendingRef.current = false;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = validateAnalysisFile(file);
    if (!result.ok) {
      toast.error(result.error);
      e.target.value = '';
      return;
    }
    setAnalysisFile(file);
    e.target.value = '';
  };

  const handleAnalyzeDocument = async (query) => {
    if (!analysisFile || !query.trim()) return;

    // Abort any in-flight analysis
    if (analyzeAbortRef.current) {
      analyzeAbortRef.current.abort();
    }
    const abortController = new AbortController();
    analyzeAbortRef.current = abortController;

    setIsAnalyzing(true);
    userScrolledUp.current = false;
    const fileToAnalyze = analysisFile;
    const userMsgId = `user-${Date.now()}`;
    const loadingId = `analysis-${Date.now()}`;

    setMessages((prev) => [
      ...prev,
      { sender: 'user', text: `📄 **${fileToAnalyze.name}**\n\n${query}`, id: userMsgId },
    ]);
    setInput('');
    setMessages((prev) => [
      ...prev,
      { sender: 'bot', text: `Analyzing ${fileToAnalyze.name}...`, id: loadingId, isLoading: true },
    ]);
    scrollToBottom(true);

    try {
      const formData = new FormData();
      formData.append('file', fileToAnalyze);
      formData.append('query', query);
      if (roomId) formData.append('room_id', roomId);

      const token = localStorage.getItem('access_token');
      const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');

      const res = await fetch(`${baseUrl}/analyze-document/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
        signal: abortController.signal,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Analysis failed');
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingId
            ? {
                ...m,
                text: data.analysis,
                isLoading: false,
                isDocumentAnalysis: true,
                filename: data.filename,
              }
            : m
        )
      );
      setAnalysisFile(null);
      scrollToBottom(true);
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingId
            ? { ...m, text: err.message || 'Failed to analyze document. Please try again.', isLoading: false }
            : m
        )
      );
      toast.error(err.message || 'Failed to analyze document.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAttachReferenceDoc = async (file) => {
    if (!roomId || !file) return;
    const result = validateAnalysisFile(file);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');

    try {
      const res = await fetch(`${baseUrl}/chatroom/${roomId}/attach-reference/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to attach reference document');
      }
      if (data.filename) {
        setReferenceDoc(data.filename);
        setMessages((prev) => [
          ...prev,
          {
            sender: 'bot',
            text: `📎 Reference document attached: **${data.filename}**\n\nI'll use this as context for all questions in this conversation.`,
            id: `ref-${Date.now()}`,
            isSystem: true,
          },
        ]);
        scrollToBottom(true);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to attach reference document.');
    }
  };

  const clearReferenceDoc = async () => {
    if (!roomId) return;
    const token = localStorage.getItem('access_token');
    const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
    try {
      await fetch(`${baseUrl}/chatroom/${roomId}/attach-reference/`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setReferenceDoc(null);
    } catch (_) {
      toast.error('Failed to remove reference document.');
    }
  };

  const copyMessageAsPlainText = (messageId, text) => {
    const plain = stripMarkdownForCopy(text);
    navigator.clipboard.writeText(plain).then(() => {
      setCopiedId(messageId);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      copy(plain);
      setCopiedId(messageId);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleStartEdit = (msg) => {
    setEditingMessageId(msg.id);
    setEditText(msg.text);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  const handleSaveEdit = () => {
    const text = editText.trim();
    if (!text || editingMessageId == null || isStreaming) return;

    const editIndex = messages.findIndex((m) => m.id === editingMessageId);
    if (editIndex === -1) return;

    const kept = messages.slice(0, editIndex);
    setEditingMessageId(null);
    setEditText('');
    setMessages(kept);
    handleSendMessage(text);
  };

  const handleThumbsUp = (id) => {
    const message = messages.find(msg => msg.id === id && msg.sender === 'bot');
    if (!message) {
      toast.error("Conversation ID not found for feedback.");
      return;
    }

    setMessages(prev => prev.map(msg =>
      msg.id === id ? { ...msg, feedback: msg.feedback === 'thumbs-up' ? null : 'thumbs-up' } : msg
    ));

    if (message.feedback === 'thumbs-up') {
      return;
    }

    updateChatFeedback({
      conversation_id: id,
      feedback: { is_thumbs_up: true, reason: 'Positive feedback' }
    })
      .unwrap()
      .then(() => {
        toast.success('Thank you for your feedback.');
      })
      .catch((err) => {
        toast.error('Failed to save feedback.');
        setMessages(prev => prev.map(msg =>
          msg.id === id ? { ...msg, feedback: null } : msg
        ));
      });
  };

  const handleThumbsDown = (id) => {
    const message = messages.find(msg => msg.id === id && msg.sender === 'bot');
    if (message) {
      if (message.feedback === 'thumbs-down') {
        setMessages(prev => prev.map(msg =>
          msg.id === id ? { ...msg, feedback: null } : msg
        ));
      } else {
        setCurrentFeedbackId(id);
        setFeedbackReason('');
        setShowFeedbackModal(true);
      }
    } else {
      toast.error("Conversation ID not found for feedback.");
    }
  };

  const submitThumbsDownFeedback = () => {
    if (!currentFeedbackId) {
      toast.error("Invalid conversation ID.");
      return;
    }

    updateChatFeedback({
      conversation_id: currentFeedbackId,
      feedback: { is_thumbs_up: false, reason: feedbackReason || 'No reason provided' }
    })
      .unwrap()
      .then(() => {
        setMessages(prev => prev.map(msg =>
          msg.id === currentFeedbackId ? { ...msg, feedback: 'thumbs-down' } : msg
        ));
        toast.success('Feedback recorded!');
        setShowFeedbackModal(false);
        setCurrentFeedbackId(null);
        setFeedbackReason('');
      })
      .catch(() => {
        toast.error('Feedback submission failed.');
      });
  };

  useEffect(() => {
    const isRoomChange = currentRoomIdRef.current !== roomId;

    if (isRoomChange) {
      currentRoomIdRef.current = roomId;
      setMessages([]);
      setInput('');
      setIsStreaming(false);
      isSendingRef.current = false;
      setEditingMessageId(null);
      setEditText('');
      navCitationsRef.current = null;
      setCitations([]);
      setHasTenantDocs(false);
      setAnswerSource('ai_knowledge');
      setReferenceDoc(null);
      setAnalysisFile(null);
    }

    if (location.state?.citations?.length) {
      navCitationsRef.current = {
        citations: location.state.citations,
        hasTenantDocs: location.state.hasTenantDocs ?? false,
        answerSource: location.state.answerSource || 'ai_knowledge',
      };
      setCitations(location.state.citations);
      setHasTenantDocs(location.state.hasTenantDocs ?? false);
      setAnswerSource(location.state.answerSource || 'ai_knowledge');
      setMessages((prev) => applyCitationsToLastBotMessage(prev, navCitationsRef.current));
    }
  }, [roomId, location.state]);

  // Restore the reference-doc chip from server state after a reload or room
  // change — without this the header silently reverts to the unattached
  // button while the backend keeps injecting the old doc into every prompt.
  useEffect(() => {
    if (!chatHistory) return;
    setReferenceDoc(chatHistory.referenceDocName || null);
  }, [chatHistory?.referenceDocName]);

  useEffect(() => {
    if (!chatHistory) return;
    if (isSendingRef.current || isStreaming) return;

    let nextMessages = chatHistory.messages;
    if (navCitationsRef.current?.citations?.length) {
      nextMessages = applyCitationsToLastBotMessage(chatHistory.messages, navCitationsRef.current);
    }

    if (chatHistory.messages.length > 0) {
      dispatch(setNewChat(false));
    }

    // Server history is the source of truth — previously we kept local state
    // whenever it was non-empty, so once it diverged the server could never
    // repair it. Re-append only local-only extras the server doesn't know
    // about (system notices, analysis exchanges not yet refetched, in-flight
    // placeholders), skipping anything already persisted server-side.
    setMessages((prev) => {
      if (prev.length === 0) return nextMessages;
      const serverIds = new Set(nextMessages.map((m) => String(m.id)));
      const serverBotTexts = new Set(
        nextMessages.filter((m) => m.sender === 'bot').map((m) => m.text)
      );
      const extras = prev.filter((m, i) => {
        if (serverIds.has(String(m.id))) return false;
        // Bot message whose exact text is persisted (e.g. a document analysis
        // saved under a server-formatted user_question) — server copy wins.
        if (m.sender === 'bot' && serverBotTexts.has(m.text)) return false;
        // User message whose reply is persisted — its exchange is server-side.
        const reply = prev[i + 1];
        if (
          m.sender === 'user' &&
          reply?.sender === 'bot' &&
          (serverIds.has(String(reply.id)) || serverBotTexts.has(reply.text))
        ) {
          return false;
        }
        return true;
      });
      return extras.length > 0 ? [...nextMessages, ...extras] : nextMessages;
    });
  }, [chatHistory, isStreaming, dispatch]);

  useEffect(() => {
    if (newChat) {
      setMessages([]);
      isSendingRef.current = false;
    }
  }, [newChat]);

  // Abort in-flight document analysis on unmount
  useEffect(() => {
    return () => {
      if (analyzeAbortRef.current) {
        analyzeAbortRef.current.abort();
      }
    };
  }, []);

  const apiDomain = (process.env.REACT_APP_BASE_URL || '')
    .replace(/\/apis\/?$/, '')
    .replace(/\/$/, '');

  return (
    <div className="chat-window chat-area d-flex flex-column h-100">
      <ChatHeader
        referenceDoc={referenceDoc}
        onAttachReferenceDoc={handleAttachReferenceDoc}
        onClearReferenceDoc={clearReferenceDoc}
      />
      <PlanBanner />
      <div
        id="messageBox"
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="messages messages-container chat-messages-container flex-grow-1 overflow-auto"
      >
        {showHistoryLoader ? (
          <ChatHistoryLoader />
        ) : messages.length === 0 ? (
          <WelcomeScreen />
        ) : (
          <div className="messages-list">
            {messages.map((msg) => (
              <div
                key={msg.id}
                ref={(el) => (messageRefs.current[msg.id] = el)}
                className={`message ${msg.sender === 'user' ? 'message-user user-message' : 'message-bot bot-message'}`}
              >
                {msg.sender === 'user' && editingMessageId === msg.id ? (
                  <div className="user-message-edit">
                    <textarea
                      className="user-edit-textarea"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <div className="user-edit-actions">
                      <button type="button" className="user-edit-save" onClick={handleSaveEdit} disabled={isStreaming || !canAsk}>
                        Save & Send
                      </button>
                      <button type="button" className="user-edit-cancel" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {msg.sender === 'bot' ? (
                      <div className="message-bot-wrapper bot-message-row">
                        <div className="bot-avatar" aria-hidden="true">
                          <img src="/edge-ai-thinking-logo.png" alt="" />
                        </div>
                        <div className="message-content bot-content">
                          {msg.isDocumentAnalysis && (
                            <div className="doc-analysis-header">
                              <span className="doc-analysis-icon">📊</span>
                              <span className="doc-analysis-label">
                                Document Analysis: {msg.filename}
                              </span>
                            </div>
                          )}
                          {(msg.streaming && !msg.text) || msg.isLoading ? (
                            <ThinkingIndicator compact />
                          ) : (
                            <>
                              <div className="bot-message-content">
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                  {msg.text || ''}
                                </ReactMarkdown>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="message-content">
                        {msg.text?.includes('📄 **') ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {msg.text}
                          </ReactMarkdown>
                        ) : (
                          msg.text
                        )}
                      </div>
                    )}
                    {msg.sender === 'bot' && msg.download_url && (
                      <a
                        href={`${apiDomain}${msg.download_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="excel-download-btn"
                      >
                        📥 Download Excel File
                      </a>
                    )}
                    {msg.sender === 'bot' && msg.citations?.length > 0 && (
                      <div className="citations-after-response">
                        <Citations
                          citations={msg.citations}
                          hasTenantDocs={msg.hasTenantDocs}
                          answerSource={msg.answerSource}
                        />
                      </div>
                    )}
                    {msg.sender === 'bot' && msg.answerSource === 'verified' && !msg.streaming && (
                      <div className="citations-after-response">
                        <ReviewedAnswerBadge />
                      </div>
                    )}
                    {msg.sender === 'bot' && msg.suggestions?.length > 0 && !msg.streaming && (
                      <div className="suggestion-chips">
                        <span className="suggestion-label">You might ask:</span>
                        {msg.suggestions.map((q, i) => (
                          <button
                            key={i}
                            className="suggestion-chip"
                            onClick={() => {
                              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, suggestions: [] } : m));
                              handleSendMessage(q);
                            }}
                            disabled={isStreaming || !canAsk}
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className={`message-actions ${msg.sender === 'user' ? 'user-message-actions' : ''}`}>
                      {msg.sender === 'user' ? (
                        <>
                          <button
                            type="button"
                            className={`copy-icon user-action-icon ${copiedId === msg.id ? 'active' : ''}`}
                            aria-label="Copy your message"
                            onClick={() => copyMessageAsPlainText(msg.id, msg.text)}
                          >
                            <Copy aria-hidden="true" focusable="false" />
                          </button>
                          <button
                            type="button"
                            className="edit-icon user-action-icon"
                            aria-label="Edit your message"
                            onClick={() => handleStartEdit(msg)}
                          >
                            <Pencil size={14} aria-hidden="true" focusable="false" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className={`copy-icon ${copiedId === msg.id ? 'active' : ''}`}
                            aria-label="Copy response"
                            onClick={() => copyMessageAsPlainText(msg.id, msg.text)}
                          >
                            <Copy aria-hidden="true" focusable="false" />
                          </button>
                          <div className="feedback-buttons">
                            <button
                              type="button"
                              className={`thumb-icon ${msg.feedback === 'thumbs-up' ? 'active' : ''}`}
                              aria-label="Mark response as helpful"
                              aria-pressed={msg.feedback === 'thumbs-up'}
                              onClick={() => handleThumbsUp(msg.id)}
                            >
                              {msg.feedback === 'thumbs-up' ? (
                                <HandThumbsUpFill aria-hidden="true" focusable="false" />
                              ) : (
                                <HandThumbsUp aria-hidden="true" focusable="false" />
                              )}
                            </button>
                            <button
                              type="button"
                              className={`thumb-icon ${msg.feedback === 'thumbs-down' ? 'active' : ''}`}
                              aria-label="Mark response as not helpful"
                              aria-pressed={msg.feedback === 'thumbs-down'}
                              onClick={() => handleThumbsDown(msg.id)}
                            >
                              {msg.feedback === 'thumbs-down' ? (
                                <HandThumbsDownFill aria-hidden="true" focusable="false" />
                              ) : (
                                <HandThumbsDown aria-hidden="true" focusable="false" />
                              )}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}

            {isStreaming && !isAnalyzing && (
              <div className="streaming-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input Area — module chips only above the bottom input bar */}
      <div className="chat-input-footer">
        {messages.length > 0 && (
          <>
            {kbStatus?.has_documents === false && (() => {
              const isTenantAdmin = (userData?.role || '').toLowerCase() === 'tenant_admin';
              const body = (
                <>
                  <span className="kb-empty-icon">💡</span>
                  <div className="kb-empty-text">
                    <strong>Full Oracle HCM expertise, ready now</strong>
                    <span>
                      {isTenantAdmin
                        ? 'Upload your implementation docs anytime for answers tailored to your setup'
                        : 'Ask your workspace admin to add your implementation docs'}
                    </span>
                  </div>
                </>
              );
              return isTenantAdmin ? (
                <Link to="/tenant-admin" className="kb-empty-banner kb-empty-banner--link">
                  {body}
                </Link>
              ) : (
                <div className="kb-empty-banner">{body}</div>
              );
            })()}
            <div className="chat-input-wrapper">
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.xlsx,.xls,.csv,.docx"
                onChange={handleFileSelect}
              />
              {analysisFile && (
                <div className="analysis-file-preview">
                  <div className="analysis-file-info">
                    <span className="analysis-file-icon">📄</span>
                    <span className="analysis-file-name">{analysisFile.name}</span>
                    <span className="analysis-file-size">
                      {(analysisFile.size / 1024).toFixed(0)}KB
                    </span>
                  </div>
                  <button
                    type="button"
                    className="analysis-file-remove"
                    onClick={() => setAnalysisFile(null)}
                  >
                    ✕
                  </button>
                </div>
              )}
              <div className="chat-input-row">
                <div className="input-wrapper input-area">
                  <Form.Control
                    as="textarea"
                    rows={1}
                    ref={textareaRef}
                    className="custom-input chat-textarea"
                    placeholder={getAnalysisPlaceholder(analysisFile, inputPlaceholder)}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isStreaming || isAnalyzing || !canAsk}
                    aria-describedby={canAsk ? undefined : ASK_BLOCKED_MESSAGE_ID}
                  />
                </div>
                <button
                  type="button"
                  className={`attach-btn ${analysisFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  title="Analyze a document"
                  disabled={isStreaming || isAnalyzing || !canAsk}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="send-btn chat-send-btn chat-send-btn-inline"
                  onClick={() => handleSendMessage()}
                  disabled={isStreaming || isAnalyzing || !canAsk}
                  aria-label="Send message"
                >
                  ↑
                </button>
              </div>
            </div>
            <div className="chat-disclaimer">
              <p>Edge AI can make mistakes. Check important info.</p>
            </div>
          </>
        )}
      </div>

      {/* Feedback Modal */}
      <Modal show={showFeedbackModal} onHide={() => setShowFeedbackModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Provide Feedback</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Why didn’t you like this response?</p>
          <Form.Control
            as="textarea"
            rows={3}
            value={feedbackReason}
            onChange={(e) => setFeedbackReason(e.target.value)}
            placeholder="e.g., Not helpful, Inaccurate"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFeedbackModal(false)}>
            Cancel
          </Button>
          <Button variant="dark" onClick={submitThumbsDownFeedback}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Room;
